import { useLoaderData } from '@remix-run/react';
import { useEffect, useMemo } from 'react';
import type { loader } from '~/root';
import logger from '~/services/logger';

export function useBrazeAnalytics() {
  const { brazeAnalytics, brazeUserId, versionInfo, environment } =
    useLoaderData<typeof loader>();

  const {
    enabled,
    api_key: brazeAPIKey,
    sdk_endpoint_url: brazeSDKEndpointURL,
    session_timeout_in_minutes: brazeSessionTimeout = 30,
    in_app_messages_enabled: inAppMessagesEnabled,
    in_app_messages_explicit_dismissal: inAppMessagesExplicitDismissal,
    in_app_messages_new_tab: inAppMessagesNewTab,
    in_app_messages_rate_limit: inAppMessagesRateLimit = 30,
  } = brazeAnalytics || {};

  const inAppMessagesOptions = useMemo(() => {
    return inAppMessagesEnabled
      ? {
          allowUserSuppliedJavascript: true,
          inAppMessageZIndex: 9999,
          requireExplicitInAppMessageDismissal:
            !!inAppMessagesExplicitDismissal,
          openInAppMessagesInNewTab: !!inAppMessagesNewTab,
          minimumIntervalBetweenTriggerActionsInSeconds: Number(
            inAppMessagesRateLimit
          ),
        }
      : {};
  }, [
    inAppMessagesEnabled,
    inAppMessagesExplicitDismissal,
    inAppMessagesNewTab,
    inAppMessagesRateLimit,
  ]);

  // https://js.appboycdn.com/web-sdk/latest/doc/modules/braze.html#initializationoptions
  const options = useMemo(() => {
    return {
      baseUrl: brazeSDKEndpointURL,
      appVersion: versionInfo?.APP_VERSION_NAME,
      enableLogging: environment !== 'production',
      sessionTimeoutInSeconds: brazeSessionTimeout * 60,
      ...inAppMessagesOptions,
    };
  }, [
    brazeSDKEndpointURL,
    versionInfo?.APP_VERSION_NAME,
    environment,
    brazeSessionTimeout,
    inAppMessagesOptions,
  ]);

  useEffect(() => {
    if (!enabled) return;

    try {
      if (!brazeAPIKey || !brazeSDKEndpointURL) {
        throw new Error(
          `Initialization failed. Plugin configuration is missing: API key - ${brazeAPIKey}, SDK endpoint - ${brazeSDKEndpointURL}`
        );
      }

      import('@braze/web-sdk').then((braze) => {
        const {
          initialize,
          automaticallyShowInAppMessages,
          openSession,
          isInitialized,
          changeUser,
          getUser,
          isPushSupported,
          requestPushPermission,
        } = braze;

        window.braze = braze;

        if (!isInitialized()) {
          initialize(brazeAPIKey, options);

          inAppMessagesEnabled && automaticallyShowInAppMessages();

          isPushSupported() && requestPushPermission();

          openSession();
        }

        if (brazeUserId && brazeUserId !== getUser()?.getUserId()) {
          changeUser(brazeUserId);
        }
      });
    } catch (error) {
      logger.error(`[Braze Analytics] ${(error as Error).message}`);
    }
  }, [
    enabled,
    brazeAPIKey,
    brazeSDKEndpointURL,
    brazeUserId,
    inAppMessagesEnabled,
    options,
  ]);
}
